import { ArrowBack, OpenInNewOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useGetDataset,
  useGetSourceDataRecord,
} from "src/views/datasets/services/DatasetServices";

import { RecordHomeKind, recordHome, recordHomeUrl } from "src/api/records";
import { tabularRowViewerUrl } from "src/reusable/Chat/citations/CitationPresentationContext";
import { useUIStore } from "../../stores/UIStore";
import { SearchResult } from "./SourceListItem";

type Props = {
  sdrId: number | null;
  sourceId: number | null;
  responseSources: SearchResult[];
};

const SourceDetailHeader = ({ sdrId, sourceId, responseSources }: Props) => {
  const { data: sourceDataRecord } = useGetSourceDataRecord(sdrId);
  const home = recordHome(sourceDataRecord);
  const datasetId =
    home?.kind === RecordHomeKind.Dataset ? home.datasetId : undefined;

  const isDocument = Boolean(sourceDataRecord?.data?.file_name);

  const { data: dataset } = useGetDataset(datasetId);
  // A folder-homed record has no dataset at all, so `dataset` never resolves
  // for it — fall back to the file name rather than leaving the heading blank.
  const title = dataset?.name ?? sourceDataRecord?.data?.file_name;

  const { closeDetailedSourceView } = useUIStore();

  // Documents open the record view (#2378 §6.3) wherever they actually live —
  // a dataset or a project folder; tabular rows (always dataset-homed) keep
  // the predictions surface. Either shape needs the loaded record — building
  // it early yields a dead tab (a route missing its id) — so it stays null
  // until the SDR arrives. The predictions route takes dataset_fk AS-IS (a
  // replicated dataset keeps its negative id there, per tabularRowViewerUrl),
  // unlike recordHomeUrl/datasetId above, which normalize via Math.abs for
  // the viewer routes — using the normalized id here would 404 or open the
  // wrong dataset for a replicated one.
  const drillDownUrl = !sourceDataRecord
    ? null
    : isDocument
      ? (home &&
          recordHomeUrl(home, sourceDataRecord.id, {
            chunkId: sourceId ?? undefined,
          })) ||
        null
      : sourceDataRecord.dataset_fk != null && sourceDataRecord.source_uid
        ? tabularRowViewerUrl(
            sourceDataRecord.dataset_fk,
            sourceDataRecord.source_uid,
          )
        : null;

  const source = responseSources.find(
    (source) => source?.data_chunk.id === sourceId,
  );

  const pageNumber = source?.data_chunk?.chunk_metadata.page_number;
  const parsedPageNumber =
    (Array.isArray(pageNumber) ? pageNumber[0] : pageNumber) || 1;

  const handleCloseSourceClick = () => {
    closeDetailedSourceView();
  };

  const handleClick = () => {
    if (!drillDownUrl) return;
    let newTabUrl = new URL(`${window.location.origin}${drillDownUrl}`);
    if (isDocument) {
      let queryParams = new URLSearchParams(newTabUrl.search);
      queryParams.set("page_number", String(parsedPageNumber));
      newTabUrl.search = queryParams.toString();
    }
    window.open(newTabUrl.toString(), "_blank");
  };

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        width: "100%",
        paddingX: 2,
        paddingY: 1.5,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: "100%" }}
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          gap={1.5}
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <Tooltip title="back" placement="left" enterDelay={0}>
            <IconButton
              onClick={handleCloseSourceClick}
              data-attr="assistant:btn:back-from-source-detail"
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>

          <Typography variant="body1" fontWeight="bold">
            {title}
          </Typography>
        </Stack>

        <Button
          color="primary"
          variant="contained"
          size="small"
          disableElevation
          fullWidth
          disabled={!drillDownUrl}
          onClick={handleClick}
          startIcon={<OpenInNewOutlined />}
          data-attr="assistant:btn:view-source-in-new-tab"
        >
          View source
        </Button>
      </Stack>
    </Box>
  );
};

export default SourceDetailHeader;
